import { useEffect, useRef } from 'react'

import type { MusicHandle } from '../audio/music'
import { playMusicLoop } from '../audio/music'
import { useGameStore } from '../store/gameStore'
import type { ResolvedTheme } from '../theme'

/**
 * Background music and its mute button.
 *
 * The track comes from the theme (`audio.music`), like every other asset —
 * a skin brings its own mood without a code change. A theme that declares
 * no music renders nothing at all: an always-visible speaker button that
 * does nothing is worse than no button.
 *
 * The audio graph is owned by this component — it is pure presentation and
 * tearing it down with the game screen is the right lifetime. The on/off
 * FLAG is not: it also covers the effects and the operator's lobby can flip
 * it over postMessage, so it lives in the store (`soundOn`) and this button
 * is just one of its two inputs.
 */
export function MusicToggle({ theme }: { theme: ResolvedTheme }): React.JSX.Element | null {
  const enabled = useGameStore((s) => s.soundOn)
  const setSoundOn = useGameStore((s) => s.setSoundOn)
  const inFreeSpins = useGameStore((s) => s.inFreeSpins)
  const handle = useRef<MusicHandle | null>(null)
  const url = theme.audio.music
  const freeUrl = theme.audio.musicFree
  const volume = theme.audio.musicVolume

  // The graph is rebuilt only when the track itself changes. Muting goes
  // through the effect below instead, so toggling does not re-fetch and
  // re-decode the file.
  const enabledAtMount = useRef(enabled)
  enabledAtMount.current = enabled
  useEffect(() => {
    if (!url) return
    const music = playMusicLoop(url, volume, enabledAtMount.current, freeUrl)
    handle.current = music
    return () => {
      handle.current = null
      music.dispose()
    }
  }, [url, freeUrl, volume])

  useEffect(() => {
    handle.current?.setEnabled(enabled)
  }, [enabled])

  // Free spins heat the music up; the summary banner cools it back down.
  // A no-op for themes without a second track.
  useEffect(() => {
    handle.current?.setMode(inFreeSpins ? 'free' : 'base')
  }, [inFreeSpins])

  if (!url) return null

  return (
    <button
      className={`music-toggle${enabled ? '' : ' music-toggle--off'}`}
      type="button"
      onClick={() => setSoundOn(!enabled)}
      aria-pressed={enabled}
      // The icon alone is not a label for a screen reader, and the title
      // is what a mouse user gets on hover.
      aria-label={enabled ? 'Turn music off' : 'Turn music on'}
      title={enabled ? 'Music on' : 'Music off'}
    >
      {/* Inline SVG rather than an emoji: it inherits the button's colour
          and renders identically on every platform, which 🔊 does not. */}
      <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" focusable="false">
        <path
          d="M3 8v4h3l4 3V5L6 8H3z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        {enabled ? (
          <>
            <path
              d="M13 7.5a3.5 3.5 0 0 1 0 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <path
              d="M15.5 5a7 7 0 0 1 0 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </>
        ) : (
          <path
            d="M13 7.5l5 5m0-5l-5 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        )}
      </svg>
    </button>
  )
}
